// =============================================================================
// SimSense Platform — Navigation Bar
// =============================================================================

import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "../hooks/useAuth";
import { useTauri } from "../hooks/useTauri";
import { DesktopStatus } from "./DesktopStatus";
import { api } from "../utils/api";
import { useToast } from "../components/Toast";
import { FriendsPanel } from "../components/FriendsPanel";
import "../components/FriendsPanel.css";
import "./NavBar.css";

interface NavItem {
  to: string;
  label: string;
  disabled?: boolean;
  /** Only show this item if user.plan matches one of these values */
  gatedTo?: string[];
}

function getNavItems(userPlan?: string): NavItem[] {
  const items: NavItem[] = [
    { to: "/tracks", label: "Tracks" },
    { to: "/cars", label: "Cars" },
    { to: "/challenges", label: "Challenges" },
    { to: "/leaderboard", label: "Leaderboard" },
    { to: "/teams", label: "Teams" },
    { to: "/marketplace", label: "Marketplace", gatedTo: ["admin", "beta_2"] },
    { to: "/setup", label: "Setup", disabled: true, gatedTo: ["admin", "beta_1"] },
  ];
  return items.filter(item => {
    if (!item.gatedTo) return true;
    return userPlan && item.gatedTo.includes(userPlan);
  });
}

export function NavBar() {
  const { user, logout, isAdmin } = useAuth();
  const isTauri = useTauri();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navItems = getNavItems(user?.plan);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <nav className={`navbar${isTauri ? " navbar--desktop" : ""}`}>
      {/* Mobile Menu Toggle */}
      <button 
        className="navbar__mobile-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {isMobileMenuOpen ? (
            <path d="M18 6L6 18M6 6l12 12" />
          ) : (
            <path d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Logo / Home link */}
      {isTauri ? (
        <NavLink
          to="/"
          end
          className={({ isActive }) => `navbar__link navbar__link--home${isActive ? " active" : ""}`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "6px" }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Dashboard
        </NavLink>
      ) : (
        <>
          <Link to="/" className="navbar__logo">
            <div className="navbar__logo-mark">S</div>
            <div className="navbar__logo-brand">
              <span className="navbar__logo-title">SimSense</span>
              <span className="navbar__logo-subtitle">Intelligence</span>
            </div>
          </Link>
          <div className="navbar__sep" />
          <NavLink
            to="/"
            end
            className={({ isActive }) => `navbar__link navbar__link--home${isActive ? " active" : ""}`}
          >
            Dashboard
          </NavLink>
        </>
      )}

      <div className="navbar__sep" />

      {/* Nav Items */}
      <div className={`navbar__items${isMobileMenuOpen ? " open" : ""}`}>
        {navItems.map((item) =>
          item.disabled ? (
            <div
              key={item.to}
              className="navbar__link navbar__link--disabled"
              title="Coming Soon"
            >
              <span>{item.label}</span>
              <span className="navbar__soon-badge">SOON</span>
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `navbar__link${isActive ? " active" : ""}`
              }
            >
              {item.label}
            </NavLink>
          )
        )}
        {isAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              `navbar__link navbar__link--admin${isActive ? " active" : ""}`
            }
          >
            Admin
          </NavLink>
        )}
      </div>

      <div className="navbar__spacer" />

      {/* Right Actions */}
      <div className="navbar__actions">
        <DesktopStatus />

        {user && (
          <>
            <div className="navbar__divider" />

            <NotificationBell />

            <FriendsButton />

            <UserMenu user={user} logout={logout} />
          </>
        )}
      </div>

      {/* Window controls are handled by window_controls.js (Rust-injected) */}
    </nav>
  );
}

function UserMenu({ user, logout }: { user: any; logout: () => void }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close on route change
  useEffect(() => { setOpen(false); }, [location]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        className="navbar__user"
        onClick={() => setOpen(!open)}
        title="Account menu"
      >
        <div className="navbar__avatar">
          {user.profilePictureUrl ? (
            <img src={user.profilePictureUrl} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; (e.target as HTMLImageElement).parentElement!.textContent = user.displayName?.substring(0, 1).toUpperCase() || "U"; }} />
          ) : (
            user.displayName?.substring(0, 1).toUpperCase() || "U"
          )}
        </div>
        <span className="navbar__username">
          {user.displayName || user.email?.split("@")[0]}
        </span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: "2px", opacity: 0.5 }}>
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="navbar__user-dropdown">
          <Link to="/profile/me" className="navbar__user-item" onClick={() => setOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            Profile
          </Link>
          <Link to="/friends" className="navbar__user-item" onClick={() => setOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            Friends
          </Link>
          <Link to="/settings" className="navbar__user-item" onClick={() => setOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
            Settings
          </Link>
          <Link to="/support" className="navbar__user-item" onClick={() => setOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
            Support
          </Link>
          <div className="navbar__user-divider" />
          <button className="navbar__user-item navbar__user-item--danger" onClick={() => { setOpen(false); logout(); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const toast = useToast();
  const [invites, setInvites] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [supportCount, setSupportCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    try {
      const [inviteData, notifyData, supportData] = await Promise.all([
        api<any[]>("/api/teams/invites/me"),
        api<any[]>("/api/notifications"),
        api<{ unreadCount: number }>("/api/support/unread-count").catch(() => ({ unreadCount: 0 }))
      ]);
      setInvites(inviteData || []);
      setNotifications(notifyData || []);
      setSupportCount(supportData?.unreadCount || 0);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  };

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, 60000);
    return () => clearInterval(timer);
  }, []);

  const unreadCount = invites.length + notifications.filter(n => !n.is_seen).length + supportCount;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showDropdown]);

  const handleInviteAction = async (inviteId: string, action: "accept" | "decline") => {
    try {
      await api(`/api/teams/invites/${inviteId}/${action}`, { method: "POST" });
      setInvites(invites.filter((i) => i.id !== inviteId));
      if (action === "accept") window.location.reload();
    } catch (err) {
      toast.error(`Failed to ${action} invitation.`);
    }
  };

  const markNoteSeen = async (id: string) => {
    try {
      await api(`/api/notifications/${id}/seen`, { method: "POST" });
      setNotifications(notifications.map(n => n.id === id ? { ...n, is_seen: true } : n));
    } catch (err) {
      console.error("Failed to mark as seen", err);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <button
        className="navbar__bell"
        onClick={() => setShowDropdown(!showDropdown)}
        title="Notifications"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && <span className="navbar__bell-dot" />}
      </button>

      {showDropdown && (
        <div className="navbar__bell-dropdown">
          <div className="navbar__bell-header">Notifications</div>
          <div className="navbar__bell-list">
            {invites.length === 0 && notifications.length === 0 && (
              <div className="navbar__bell-empty">No new notifications</div>
            )}
            
            {/* Team Invites */}
            {invites.map((invite) => (
              <div key={invite.id} className="navbar__invite">
                <p className="navbar__invite-text">Invited to join <strong>{invite.team.name}</strong></p>
                <div className="navbar__invite-actions">
                  <button className="navbar__invite-btn navbar__invite-btn--accept" onClick={() => handleInviteAction(invite.id, "accept")}>Accept</button>
                  <button className="navbar__invite-btn navbar__invite-btn--decline" onClick={() => handleInviteAction(invite.id, "decline")}>Decline</button>
                </div>
              </div>
            ))}

            {/* Support Ticket Updates */}
            {supportCount > 0 && (
              <Link to="/support" style={{ textDecoration: "none" }} onClick={() => setShowDropdown(false)}>
                <div className="navbar__invite" style={{ cursor: "pointer" }}>
                  <div className="navbar__notif-tag">Support</div>
                  <p className="navbar__notif-title">
                    {supportCount} ticket{supportCount > 1 ? 's' : ''} with admin response
                  </p>
                  <div className="navbar__notif-sub">Click to view your tickets</div>
                </div>
              </Link>
            )}

            {/* System Notifications */}
            {notifications.map((n) => (
              <div key={n.id} className={`navbar__invite ${!n.is_seen ? 'navbar__invite--unread' : ''}`} style={{ opacity: n.is_seen ? 0.6 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="navbar__notif-tag">{n.type}</div>
                    <p className="navbar__notif-title">{n.title}</p>
                  </div>
                  {!n.is_seen && <button onClick={() => markNoteSeen(n.id)} className="navbar__notif-mark-seen">Mark seen</button>}
                </div>
                <div className="navbar__notif-body">
                  {n.body.split('\n')[0].substring(0, 100)}...
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// OT-246: Friends icon button + slide-out panel (Steam-style)
function FriendsButton() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [hasRequests, setHasRequests] = useState(false);

  // Check for pending requests on mount
  useEffect(() => {
    api<{ incoming: any[] }>("/api/friends/requests/me")
      .then(data => setHasRequests((data?.incoming?.length || 0) > 0))
      .catch(() => {});
  }, []);

  return (
    <>
      <button
        className="navbar__friends-btn"
        onClick={() => setPanelOpen(!panelOpen)}
        title="Friends"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        {hasRequests && <span className="navbar__friends-badge" />}
      </button>
      <FriendsPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
    </>
  );
}
